import { Injectable } from '@angular/core';
import { Source } from '@cargos/sprintpay-models';
import { StorageService, StorageTypes, TokenService } from '@cargos/sprintpay-services';
import { BehaviorSubject } from 'rxjs';
import { ThemeType } from '../models';
import { OperatingHour, WorkingHours } from '../models/config';
import { InitialFacilityConfig } from '../models/config/facility-config';

export interface CustomerServiceInfo {
    nameSection?: string;
    email?: string;
    phone?: string;
    timeZone?: string;
    operatingHours?: OperatingHour[];
}

export enum FacilitySource {
    SPRINTPAY = 'sprintpay',
    CARGOSPRINT = 'cargosprint',
    ALLIANCE = 'alliance',
}

@Injectable({
    providedIn: 'root',
})
export class CurrentFacilityService {
    private _facilitySource: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);
    private indexTitle = '';
    private urlFavIcon = '';
    private urlAdministrative = '';
    private alternativeLogoLightUrl = '';
    private alternativeLogoDarkUrl = '';
    private alternativeLogoWidth = '';
    private alternativeLogoHeight = '';
    private mainLogoLightUrl = '';
    private mainLogoDarkUrl = '';
    private mainLogoWidth = '';
    private mainLogoHeight = '';
    private _setMainLogoToHeader = false;
    private _setMainLogoToLogin = false;
    private _setAlternativeLogoToHeader = false;
    private _setAlternativeLogoToLogin = false;
    private facilityName = '';
    private facilityEmail = '';
    private facilityTermsOfUse = '';
    private facilityPrivacyAndPolicyURL = '';
    private faqExternalURL: InitialFacilityConfig['faqExternalURL'] = {};
    private facilityCustomerServiceInfo: CustomerServiceInfo | null = null;
    private paidToFacilityId: number | undefined;
    private logoWidth = '';
    private logoHeight = '';

    constructor(
        private storageService: StorageService,
        private tokenService: TokenService
    ) {
        if (!this.getFacilitySource()) {
            const facility = this.storageService.getElement<string | null>('SOURCE', StorageTypes.SESSION);
            this.setFacilitySource(facility);
        }
    }

    /**
     * Sets the initial configuration for the Cargo Facility.
     *
     * @param indexTitle - The title of the current HTML document.
     * @param urlFavIcon - The path to the favicon image for the current HTML document. Example: './assets/{facility}/img/{facility}-favicon.png'
     * @param urlLogo - The URL of the light logo image for the current facility. Example: 'assets/{facility}/img/{facility}-logo-light.png'
     * @param urlDarkLogo - (optional) The URL of the dark logo image for the current facility. Example: 'assets/{facility}/img/{facility}-logo-dark.png'
     * @param logoWidth - (optional) The width of the logo image in pixels.
     * @param logoHeight - (optional) The height of the logo image in pixels.
     * @param urlAdministrative - (optional) The URL for the administrative login.
     * @param name - (optional) The name of the facility.
     * @param email - (optional) The email address of the facility.
     * @param termsOfUseURL - (optional) The URL of the terms of use web page for the facility.
     * @param privacyAndPolicyURL - (optional) The URL of the privacy and policy web page for the facility.
     * @param faqExternalURL - (optional): URL of the external FAQ page (can be a .pdf or web page).
     */
    setInitialConfiguration(config: InitialFacilityConfig): void {
        this.setInitialDisplayConfiguration(config);
        this.setInitialLogoConfiguration(config);
        this.setInitialFacilityDetails(config);
        this.setInitialExternalConfiguration(config);
    }

    private setInitialDisplayConfiguration(config: InitialFacilityConfig): void {
        if (config.indexTitle) this.setIndexTitle(config.indexTitle);
        if (config.urlFavIcon) this.setUrlFavIcon(config.urlFavIcon);
    }

    private setInitialLogoConfiguration(config: InitialFacilityConfig): void {
        if (config.alternativeLogoLightUrl !== undefined)
            this.setAlternativeLogoLightUrl(config.alternativeLogoLightUrl);
        if (config.alternativeLogoDarkUrl !== undefined) this.setAlternativeLogoDarkUrl(config.alternativeLogoDarkUrl);
        if (config.alternativeLogoWidth) this.setAlternativeLogoWidth(config.alternativeLogoWidth);
        if (config.alternativeLogoHeight) this.setAlternativeLogoHeight(config.alternativeLogoHeight);
        if (config.mainLogoLightUrl !== undefined) this.setMainLogoLightUrl(config.mainLogoLightUrl);
        if (config.mainLogoDarkUrl !== undefined) this.setMainLogoDarkUrl(config.mainLogoDarkUrl);
        if (config.mainLogoWidth !== undefined) this.setMainLogoWidth(config.mainLogoWidth);
        if (config.mainLogoHeight !== undefined) this.setMainLogoHeight(config.mainLogoHeight);
        if (config.setMainLogoToHeader !== undefined) this.setMainLogoToHeader(config.setMainLogoToHeader);
        if (config.setMainLogoToLogin !== undefined) this.setMainLogoToLogin(config.setMainLogoToLogin);
        if (config.setAlternativeLogoToHeader !== undefined)
            this.setAlternativeLogoToHeader(config.setAlternativeLogoToHeader);
        if (config.setAlternativeLogoToLogin !== undefined)
            this.setAlternativeLogoToLogin(config.setAlternativeLogoToLogin);
    }

    private setInitialFacilityDetails(config: InitialFacilityConfig): void {
        if (config.urlAdministrative !== undefined) this.setUrlAdministrative(config.urlAdministrative);
        if (config.name !== undefined) this.setFacilityName(config.name);
        if (config.email !== undefined) this.setFacilityEmail(config.email);
        if (config.termsOfUseURL !== undefined) this.setTermsOfUse(config.termsOfUseURL);
        if (config.privacyAndPolicyURL !== undefined) this.setPrivacyAndPolicyURL(config.privacyAndPolicyURL);
    }

    private setInitialExternalConfiguration(config: InitialFacilityConfig): void {
        if (config.faqExternalURL !== undefined)
            this.setFAQExternalURL({
                externalURL: config.faqExternalURL?.externalURL || '',
                pdfURL: config.faqExternalURL?.pdfURL,
            });
        if (config.paidToFacilityId !== undefined) this.setPaidToFacilityId(config.paidToFacilityId);
        if (config.facilitySource !== undefined) this.setFacilitySource(config.facilitySource);
    }

    setFacilitySource(sourceName: string | null): void {
        this._facilitySource.next(sourceName);
        this.storageService.saveElement('SOURCE', sourceName, StorageTypes.SESSION);
    }

    getFacilitySource(): string {
        return this._facilitySource.value || '';
    }

    setIndexTitle(indexTitle: string): void {
        this.indexTitle = indexTitle;
    }

    getIndexTitle(): string {
        return this.indexTitle;
    }
    setAlternativeLogoLightUrl(url: string | null): void {
        this.alternativeLogoLightUrl = url || '';
    }

    getAlternativeLogoLightUrl(): string {
        return this.alternativeLogoLightUrl;
    }

    setAlternativeLogoDarkUrl(url: string | null): void {
        this.alternativeLogoDarkUrl = url || '';
    }

    getAlternativeLogoDarkUrl(): string {
        return this.alternativeLogoDarkUrl;
    }

    setAlternativeLogoWidth(width: string | null): void {
        this.alternativeLogoWidth = width || '';
    }

    getAlternativeLogoWidth(): string {
        return this.alternativeLogoWidth;
    }

    setAlternativeLogoHeight(height: string | null): void {
        this.alternativeLogoHeight = height || '';
    }

    getAlternativeLogoHeight(): string {
        return this.alternativeLogoHeight;
    }

    getAlternativeLogoByMode(mode: ThemeType): string {
        const preferredSrc =
            mode === ThemeType.dark ? this.getAlternativeLogoDarkUrl() : this.getAlternativeLogoLightUrl();
        const preferred = this.getImageSrc(preferredSrc);
        if (preferred) return preferred;

        const fallbackSrc = this.getDefaultLogoByMode(mode);
        const fallback = this.getImageSrc(fallbackSrc);
        return fallback;
    }

    setMainLogoLightUrl(url: string | null): void {
        this.mainLogoLightUrl = url || '';
    }

    getMainLogoLightUrl(): string {
        return this.mainLogoLightUrl;
    }

    setMainLogoDarkUrl(url: string | null): void {
        this.mainLogoDarkUrl = url || '';
    }

    getMainLogoDarkUrl(): string {
        return this.mainLogoDarkUrl;
    }

    setMainLogoWidth(width: string | null): void {
        this.mainLogoWidth = width || '';
    }

    getMainLogoWidth(): string {
        return this.mainLogoWidth;
    }

    setMainLogoHeight(height: string | null): void {
        this.mainLogoHeight = height || '';
    }

    getMainLogoHeight(): string {
        return this.mainLogoHeight;
    }

    getMainLogoByMode(mode: ThemeType): string {
        const preferredSrc = mode === ThemeType.dark ? this.getMainLogoDarkUrl() : this.getMainLogoLightUrl();
        const preferred = this.getImageSrc(preferredSrc);
        if (preferred) return preferred;

        const fallbackSrc = this.getDefaultLogoByMode(mode);
        const fallback = this.getImageSrc(fallbackSrc);
        return fallback;
    }

    setMainLogoToHeader(value: boolean): void {
        this._setMainLogoToHeader = value;
    }

    getMainLogoToHeader(): boolean {
        return this._setMainLogoToHeader;
    }

    setMainLogoToLogin(value: boolean): void {
        this._setMainLogoToLogin = value;
    }

    getMainLogoToLogin(): boolean {
        return this._setMainLogoToLogin;
    }

    setAlternativeLogoToHeader(value: boolean): void {
        this._setAlternativeLogoToHeader = value;
    }

    getAlternativeLogoToHeader(): boolean {
        return this._setAlternativeLogoToHeader;
    }

    setAlternativeLogoToLogin(value: boolean): void {
        this._setAlternativeLogoToLogin = value;
    }

    getAlternativeLogoToLogin(): boolean {
        return this._setAlternativeLogoToLogin;
    }

    getLogoHeightForLogin(): string {
        if (this.getMainLogoToLogin()) {
            return this.getMainLogoHeight();
        }
        if (this.getAlternativeLogoToLogin()) {
            return this.getAlternativeLogoHeight();
        }
        return this.getMainLogoHeight();
    }

    getLogoWidthForLogin(): string {
        if (this.getMainLogoToLogin()) {
            return this.getMainLogoWidth();
        }
        if (this.getAlternativeLogoToLogin()) {
            return this.getAlternativeLogoWidth();
        }
        return this.getMainLogoWidth();
    }

    getLogoHeightForHeader(): string {
        if (this.getMainLogoToHeader()) {
            return this.getMainLogoHeight();
        }
        if (this.getAlternativeLogoToHeader()) {
            return this.getAlternativeLogoHeight();
        }
        return this.getAlternativeLogoHeight();
    }

    getLogoWidthForHeader(): string {
        if (this.getMainLogoToHeader()) {
            return this.getMainLogoWidth();
        }
        if (this.getAlternativeLogoToHeader()) {
            return this.getAlternativeLogoWidth();
        }
        return this.getAlternativeLogoWidth();
    }

    private getLogoWithFallback(
        preferredUrl: string | null | undefined,
        fallbackUrl: string | null | undefined
    ): string {
        if (preferredUrl) {
            return preferredUrl;
        }
        return fallbackUrl || '';
    }

    getDefaultLogoByMode(mode: ThemeType): string {
        const preferredUrl = mode === ThemeType.dark ? this.getMainLogoDarkUrl() : this.getMainLogoLightUrl();
        const fallbackUrl = mode === ThemeType.dark ? this.getMainLogoLightUrl() : this.getMainLogoDarkUrl();
        return this.getLogoWithFallback(preferredUrl, fallbackUrl);
    }

    private getImageSrc(imageUrl: string): string {
        if (!imageUrl) return '';
        return imageUrl.startsWith('http') ? imageUrl : `assets/${imageUrl}`;
    }

    setUrlAdministrative(urlAdministrative: string): void {
        this.urlAdministrative = urlAdministrative;
    }

    getUrlAdministrative(): string {
        return this.urlAdministrative;
    }

    setUrlFavIcon(urlFavIcon: string): void {
        this.urlFavIcon = urlFavIcon;
    }

    getUrlFavIcon(): string {
        return this.urlFavIcon;
    }

    setFacilityName(name: string): void {
        this.facilityName = name;
    }

    getFacilityName(): string {
        return this.facilityName;
    }

    setFacilityEmail(email: string): void {
        this.facilityEmail = email;
    }

    getFacilityEmail(): string {
        return this.facilityEmail;
    }

    getSource(): Source | null {
        const source = this.tokenService.getDecodedToken().SOURCE;

        try {
            return source ? (JSON.parse(source) as Source) : null;
        } catch (error) {
            return null;
        }
    }

    getSourceId(): number | null {
        return this.getSource()?.id || null;
    }

    setTermsOfUse(termsOfUseURL: string): void {
        this.facilityTermsOfUse = termsOfUseURL;
    }

    getTermsOfUseURL(): string {
        return this.facilityTermsOfUse;
    }

    setPrivacyAndPolicyURL(privacyAndPolicyURL: string): void {
        this.facilityPrivacyAndPolicyURL = privacyAndPolicyURL;
    }

    getPrivacyAndPolicyURL(): string {
        return this.facilityPrivacyAndPolicyURL;
    }

    setCustomerServiceInfo(info: CustomerServiceInfo): void {
        this.facilityCustomerServiceInfo = info;
    }

    getCustomerServiceInfo(): CustomerServiceInfo | null {
        return this.facilityCustomerServiceInfo;
    }

    setFAQExternalURL(faqExternalURL: InitialFacilityConfig['faqExternalURL']): void {
        this.faqExternalURL = faqExternalURL;
    }

    getFAQExternalURL(): InitialFacilityConfig['faqExternalURL'] {
        return this.faqExternalURL;
    }

    setPaidToFacilityId(paidToFacilityId: number | undefined): void {
        this.paidToFacilityId = paidToFacilityId;
    }

    getPaidToFacilityId(): number | undefined {
        return this.paidToFacilityId;
    }

    getOperatingHours(workingHours: WorkingHours | undefined): OperatingHour[] {
        if (!workingHours?.ranges?.length) return [];

        return workingHours.ranges.map((range) => {
            const fromDay = range.daysRange?.from || '';
            const toDay = range.daysRange?.to || '';
            const days = fromDay && toDay ? `${fromDay} to ${toDay}` : '';

            const startTime = this.formatTime(range.start);
            const endTime = this.formatTime(range.end);
            const schedule = startTime && endTime ? `${startTime} to ${endTime}` : '';

            return { days, schedule };
        });
    }

    formatTime(time?: string): string {
        if (!time) return '';
        const [hourStr, minuteStr] = time.split(':');
        const hour = Number(hourStr);
        const minute = Number(minuteStr);
        if (isNaN(hour) || isNaN(minute)) return '';

        const period = hour >= 12 ? 'PM' : 'AM';
        const formattedHour = hour % 12 || 12;
        return `${formattedHour}:${minute.toString().padStart(2, '0')} ${period}`;
    }

    setLogoWidth(logoWidth: string): void {
        this.logoWidth = logoWidth;
    }

    getLogoWidth(): string {
        return this.logoWidth;
    }

    setLogoHeight(logoHeight: string): void {
        this.logoHeight = logoHeight;
    }

    getLogoHeight(): string {
        return this.logoHeight;
    }
}
